/**
 * TODO:
 * 1. Реализовать функцию getLayerData для получения GeoJSON слоя
 * 2. Реализовать функцию getAreaInfo для получения информации об участке
 * 3. Реализовать функцию getFilterOptions для получения опций фильтров
 * 4. Реализовать функцию downloadGPX для скачивания GPX файла
 * 5. Реализовать функцию downloadAtlas для скачивания страниц атласа
 * 6. Добавить обработку ошибок для всех запросов
 * 7. Добавить обработку состояния загрузки
 */

let API_BASE_URL = 'http://localhost:8088'; // Будет переопределяться через параметры

// Установка базового URL API при инициализации
export function setApiBaseUrl(url) {
    API_BASE_URL = url;
}

/**
 * Получение данных слоя в формате GeoJSON
 * @param {string} layerId - ID слоя (landspol, zonespol, etc.)
 * @param {string} date - Дата в формате DD.MM.YYYY или 'nodate'
 * @param {Object} filters - Объект с фильтрами для query параметров
 * @param {string} apiUrl - URL API бэкенда
 * @returns {Promise<Object>} GeoJSON объект
 */
export async function getLayerData(layerId, date, filters = {}, apiUrl = API_BASE_URL) {
    try {
        // Формирование query строки из фильтров
        const queryParams = new URLSearchParams();
        Object.keys(filters).forEach(key => {
            if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
                queryParams.append(key, filters[key]);
            }
        });

        const queryString = queryParams.toString();
        const datePath = date ? `/${date}` : '';
        const url = `${apiUrl}/gis/${layerId}${datePath}${queryString ? '?' + queryString : ''}`;

        const response = await fetch(url, {
            credentials: 'omit', // Публичный доступ без авторизации
            headers: {
                'Accept': 'application/json'
            }
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(`Error loading layer ${layerId}:`, response.status, errorText);
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Проверяем наличие ошибок в ответе
        if (result.flash && result.flash.length > 0) {
            result.flash.forEach(msg => {
                if (msg.level === 'e') {
                    console.error(`API error for layer ${layerId}:`, msg.message);
                }
            });
        }

        return result;
    } catch (error) {
        console.error(`Error loading layer data for ${layerId}:`, error);
        return {type: 'FeatureCollection', features: []};
    }
}

/**
 * Получение информации об участке
 * @param {string} layerId - ID слоя (landspol или ecospol)
 * @param {string|number} id - ID участка
 * @param {string} apiUrl - URL API бэкенда
 * @returns {Promise<Object>} Объект с информацией об участке
 */
export async function getAreaInfo(layerId, id, apiUrl = API_BASE_URL) {
    try {
        const url = `${apiUrl}/gis/${layerId}/desc/${id}`;

        const response = await fetch(url, {
            credentials: 'omit', // Публичный доступ без авторизации
            headers: {
                'Accept': 'application/json'
            }
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(`Error loading area info ${layerId}/${id}:`, response.status, errorText);

            if (response.status === 404) {
                return null; // Участок не найден
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Проверяем наличие ошибок в ответе
        if (result.flash && result.flash.length > 0) {
            result.flash.forEach(msg => {
                if (msg.level === 'e') {
                    console.error(`API error for area ${layerId}/${id}:`, msg.message);
                }
            });
        }

        // Преобразование данных в нужный формат
        const data = result.data || {};
        return {
            name: data.label || data.name || data.land_name || data.economy_name || 'Не указано',
            area: data.area || data.land_area || data.economy_area || null,
            description: data.description || data.land_description || data.economy_description || null,
            permitPoints: data.permit_points || data.permits || [],
            hasAtlas: data.has_atlas || false,
            id: id,
            layerId: layerId
        };
    } catch (error) {
        console.error(`Error loading area info for ${layerId}/${id}:`, error);
        return null;
    }
}

/**
 * Получение опций для фильтра
 * Формат запроса аналогичен client/src/common/Funcs.js getRefData
 * @param {string} filterName - Имя фильтра (region, users, ecos, lands)
 * @param {Object} parentFilters - Фильтры родительских уровней (как в client panelFilters)
 * @param {string} apiUrl - URL API бэкенда
 * @returns {Promise<Array>} Массив опций {value, title}
 */
const descriptionss = {
    region: {tableId: 'regions', idField: 'oktmo', titleField: 'region_name', active: false},
    users: {tableId: 'hunt_users_ref', idField: 'huntuser_id', titleField: 'user_name', active: true},
    ecos: {tableId: 'economies_ref', idField: 'economy_id', titleField: 'economy_name', active: true},
    lands: {tableId: 'lands_ref', idField: 'land_id', titleField: 'label', active: true}
}

export async function getFilterOptions(filterName, parentFilters = {}, apiUrl = API_BASE_URL) {
    try {
        const {tableId, idField, titleField, active} = descriptionss[filterName]
        const filterParts = [];

        if (parentFilters.region && filterName !== 'region') {
            filterParts.push(`oktmo like '%${parentFilters.region}%'`);
        }

        if (parentFilters.users) switch (filterName) {
            case'ecos':
                filterParts.push(`economy_huntuser=${parentFilters.users}`);
                break;
            case 'lands':
                filterParts.push(`land_huntuser=${parentFilters.users}`);
                break;
            default:
        }

        if (parentFilters.ecos && filterName === 'lands') {
            filterParts.push(`land_economy=${parentFilters.ecos}`);
        }

        if (active) filterParts.push(`active=true`);
        let filterString = (filterParts.length > 0) ?
            filterParts.join(' and ') : '1=1';

        let url = `${apiUrl}/API/data/${tableId}/ref/${idField}/${titleField}`;
        const queryParams = new URLSearchParams();
        queryParams.append('filter', filterString);

        if (queryParams.toString()) {
            url += `?${queryParams.toString()}`;
        }

        const response = await fetch(url, {
            credentials: 'omit', // Не передаем cookies для публичного доступа (как в клиенте credentials: 'include' для авторизованных)
            headers: {
                'Accept': 'application/json'
            }
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(`Error loading ${filterName}:`, response.status, errorText);

            // Если получили ошибку авторизации, возвращаем пустой массив
            if (response.status === 401 || response.status === 403) {
                console.warn(`Access denied for ${filterName}. Endpoint may require authentication.`);
                return [];
            }
            throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
        }

        const result = await response.json();

        if (result.flash && result.flash.length > 0) {
            result.flash.forEach(msg => {
                if (msg.level === 'e') {
                    console.error(`API error for ${filterName}:`, msg.message);
                }
            });
        }

        // Если ответ содержит page (редирект на страницу авторизации), обрабатываем
        if (result.page) {
            console.warn(`Server requested page redirect: ${result.page}`);
            return [];
        }

        // Возвращаем массив опций в формате {value, title}
        return result.data || [];
    } catch (error) {
        console.error(`Error loading filter options for ${filterName}:`, error);
        return [];
    }
}

/**
 * Получение геометрии объекта по ID
 * @param {string} tableName - Имя таблицы (admpol, landspol, etc.)
 * @param {string|number} id - ID объекта
 * @param {string} apiUrl - URL API бэкенда
 * @param {boolean} active - Фильтр по активности
 * @returns {Promise<Object>} GeoJSON объект с геометрией
 */
export async function getGeomById(tableName, id, apiUrl = API_BASE_URL, active = false) {
  try {
    const url = `${apiUrl}/gis/${tableName}/feature/${id}?props=false${active ? '&a=1' : ''}`;
    
    const response = await fetch(url, {
      credentials: 'omit',
      headers: {
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error loading geometry for ${tableName}/${id}:`, response.status, errorText);
      
      if (response.status === 404) {
        return null;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.flash && result.flash.length > 0) {
      result.flash.forEach(msg => {
        if (msg.level === 'e') {
          console.error(`API error for geometry ${tableName}/${id}:`, msg.message);
        }
      });
    }
    
    // Добавляем source к каждой feature
    if (result.features) {
      result.features.forEach(f => {
        if (f.properties) {
          f.properties.source = tableName;
        }
      });
    }
    
    return result;
  } catch (error) {
    console.error(`Error loading geometry for ${tableName}/${id}:`, error);
    return null;
  }
}

/**
 * Скачивание GPX файла границы участка
 * @param {string} tableName - Имя таблицы (landspol, ecospol)
 * @param {string} fieldId - Имя поля ID
 * @param {string|number} id - ID участка
 * @param {string} apiUrl - URL API бэкенда
 * @returns {Promise<string>} GPX строка (можно сохранить в файл)
 */
export async function downloadGPX(tableName, fieldId, id, apiUrl = API_BASE_URL) {
    try {

        // Используем endpoint /geojson/shp для получения данных
        // Или можно создать отдельный endpoint для GPX
        const url = `${apiUrl}/geojson/shp/${tableName}/${fieldId}/${id}`;

        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Если это ZIP файл (shp), нужно распаковать и конвертировать в GPX
        // Или использовать прямой endpoint для GPX, если есть
        // Пока возвращаем как blob
        const blob = await response.blob();

        return blob;
    } catch (error) {
        console.error(`Error downloading GPX for ${tableName}/${id}:`, error);
        throw error;
    }
}

/**
 * Получение информации о пункте выдачи
 * @param {string|number} pickupId - ID пункта выдачи
 * @param {string} apiUrl - URL API бэкенда
 * @returns {Promise<Object|null>} Данные пункта (huntuser_name, economy_name, pickup_address, pickup_desc, region_name)
 */
export async function getPickupInfo(pickupId, apiUrl = API_BASE_URL) {
  try {
    const url = `${apiUrl}/gis/pickuppnt/desc/${pickupId}`;
    const response = await fetch(url, {
      credentials: 'omit',
      headers: { 'Accept': 'application/json' }
    });
    if (!response.ok) {
      if (response.status == 404) return null;
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const result = await response.json();
    if (result.flash?.some(m => m.level == 'e')) {
      result.flash.filter(m => m.level == 'e').forEach(m => console.error('API error:', m.message));
    }
    return result.data || null;
  } catch (error) {
    console.error(`Error loading pickup info for ${pickupId}:`, error);
    return null;
  }
}

/**
 * Получение информации об участке из lands_view
 * @param {string|number} landId - ID участка
 * @param {string} apiUrl - URL API бэкенда
 * @returns {Promise<Object>} Данные участка
 */
export async function getLandInfo(landId, apiUrl = API_BASE_URL) {
    try {
        const url = `${apiUrl}/API/data/lands_view/row/${landId}`;
        
        const response = await fetch(url, {
            credentials: 'omit',
            headers: {
                'Accept': 'application/json'
            }
        });
        
        if (!response.ok) {
            if (response.status === 404) {
                return null;
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.flash && result.flash.length > 0) {
            result.flash.forEach(msg => {
                if (msg.level === 'e') {
                    console.error(`API error for land ${landId}:`, msg.message);
                }
            });
        }
        
        return result.data || null;
    } catch (error) {
        console.error(`Error loading land info for ${landId}:`, error);
        throw error;
    }
}

/**
 * Получение списка документов (атлас и описание границ)
 * @param {string|number} landId - ID участка
 * @param {string} apiUrl - URL API бэкенда
 * @returns {Promise<Array>} Массив документов
 */
export async function getLandDocuments(landId, apiUrl = API_BASE_URL) {
    try {
        const url = `${apiUrl}/API/data/landdocuments/view?doc_land=${landId}&doc_active=true&order_value=60`;
        
        const response = await fetch(url, {
            credentials: 'omit',
            headers: {
                'Accept': 'application/json'
            }
        });
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.flash && result.flash.length > 0) {
            result.flash.forEach(msg => {
                if (msg.level === 'e') {
                    console.error(`API error for land documents ${landId}:`, msg.message);
                }
            });
        }
        
        return result.data || [];
    } catch (error) {
        console.error(`Error loading land documents for ${landId}:`, error);
        return [];
    }
}

/**
 * Скачивание файла (GPX, атлас или описание)
 * @param {string} filePath - Путь к файлу на сервере
 * @param {string} fileName - Имя файла для скачивания
 * @param {string} apiUrl - URL API бэкенда
 * @returns {Promise<void>}
 */
export async function downloadFile(filePath, fileName, apiUrl = API_BASE_URL) {
    try {
        // Документы отдаются бэкендом через роут /files/*
        // Приводим путь к web-формату и избегаем дублирования /files
        const normalizedPath = String(filePath || '')
            .replace(/\\/g, '/')
            .replace(/^\/+/, '')
            .replace(/^files\/+/i, '');
        const url = `${apiUrl.replace(/\/+$/, '')}/files/documents/${normalizedPath}`;
        
        const response = await fetch(url, {
            credentials: 'omit'
        });
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = fileName || filePath.split('/').pop();
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
        console.error(`Error downloading file ${filePath}:`, error);
        throw error;
    }
}

function transliterateToLatin(value) {
    const map = {
        а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i',
        й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't',
        у: 'u', ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '',
        э: 'e', ю: 'yu', я: 'ya'
    };

    return String(value || '')
        .toLowerCase()
        .split('')
        .map((char) => map[char] !== undefined ? map[char] : char)
        .join('');
}

function buildGpxFileName(landId, landName) {
    const transliterated = transliterateToLatin(landName);
    const safeName = transliterated.replace(/[^a-z0-9]/g, '');
    if (safeName) return `${safeName}.gpx`;
    return `land${String(landId || '').replace(/[^0-9]/g, '') || '0'}.gpx`;
}

/**
 * Скачивание GPX границы участка
 * @param {string|number} landId - ID участка
 * @param {string} landName - Название участка
 * @param {string} apiUrl - URL API бэкенда
 * @returns {Promise<void>}
 */
export async function downloadLandGPX(landId, landName = '', apiUrl = API_BASE_URL) {
    try {
        const url = `${apiUrl}/gis/landspol/gpx/land_id/${landId}`;
        
        const response = await fetch(url, {
            credentials: 'omit',
            headers: {
                'Accept': 'application/json'
            }
        });
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const gpxString = result?.gpx;
        if (!gpxString) {
            throw new Error('Empty GPX data');
        }

        const fileName = buildGpxFileName(landId, landName);
        const blob = new Blob([gpxString], {type: 'application/gpx+xml;charset=utf-8'});
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
        console.error(`Error downloading GPX for land ${landId}:`, error);
        throw error;
    }
}

/**
 * Скачивание страниц атласа
 * @param {string} filePath - Путь к файлу атласа
 * @param {string} fileName - Имя файла для скачивания
 * @param {string} apiUrl - URL API бэкенда
 * @returns {Promise<void>}
 */
export async function downloadAtlas(filePath, fileName, apiUrl = API_BASE_URL) {
    return downloadFile(filePath, fileName, apiUrl);
}


export default {
    getLayerData,
    getAreaInfo,
    getPickupInfo,
    getFilterOptions,
    downloadGPX,
    downloadAtlas,
    getLandInfo,
    getLandDocuments,
    downloadLandGPX,
    downloadFile,
    setApiBaseUrl
};

